/**
 * The transition page's right-hand panel: a section's unanswered questions,
 * from the Bridge Figma file's Happy Path (frames 263:1474, 276:1985,
 * 278:2991). Questions are in the order they were asked (the API's), so one a
 * re-draft raises arrives at the bottom, marked New, and nothing above the
 * user's place moves; a note above the list says how many arrived and jumps
 * to them. Answered and skipped questions stay in their place.
 * Any question can be answered again, or after a skip (Edit / Answer); a
 * changed answer doesn't re-draft the section, its next draft reads it.
 * Typed answers live with the page (see TransitWorkspace), so closing the
 * panel doesn't lose them.
 *
 * Each question has its own Submit and Skip, as the API answers one question
 * at a time. Answering a section's last draft question re-drafts it on the
 * server; the page's drafting poll picks that up.
 */
import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { Check, Loader2, MessageCircleQuestion } from 'lucide-react'
import { Button, Textarea } from '../ui'
import { cn } from '../../lib/utils'
import SvgIcon from './SvgIcon'
import { transitApi } from '../../services/transit'
import type { GraphNode, InfoRequest } from '../../types/transit'

export interface Draft {
  /** Choices picked; several can apply. */
  picked: string[]
  /** "Type another response", or the whole answer when there are no choices. */
  typed: string
  typing: boolean
}

const EMPTY: Draft = { picked: [], typed: '', typing: false }

/** A band sweeping across while something saves (PendingJobCard's shimmer,
 * tinted with the primary: a white band doesn't show on the white panel). */
function Shimmer() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 bg-gradient-to-r from-transparent via-primary/[0.14] to-transparent animate-shimmer motion-reduce:hidden dark:via-white/10" />
  )
}

/** The answer the API stores: the picks, then anything typed, "; "-joined. */
function answerOf(d: Draft): string {
  return [...d.picked, d.typed.trim()].filter(Boolean).join('; ')
}

/** A stored answer split back into its picked choices and anything typed. */
function draftOf(r: InfoRequest): Draft {
  const parts = (r.answer ?? '').split(';').map((p) => p.trim()).filter(Boolean)
  const typed = parts.filter((p) => !r.choices.includes(p)).join('; ')
  return { picked: r.choices.filter((c) => parts.includes(c)), typed, typing: typed !== '' || r.choices.length === 0 }
}

/** The statuses a section can be re-drafted from to pick up a changed answer.
 * Not done: re-drafting an approved section takes its approval away. */
const REDRAFTABLE = ['review', 'needs_input', 'failed']

export default function QuestionsPanel({ node, waitsOn, requests, drafts, setDrafts, onChange }: {
  node: GraphNode
  /** Names of the sections this one waits on, for the "waiting" next step. */
  waitsOn: string[]
  /** This node's questions, all statuses. */
  requests: InfoRequest[]
  /** Unsent answers by question id, owned by the page so they outlive the panel. */
  drafts: Record<string, Draft>
  setDrafts: Dispatch<SetStateAction<Record<string, Draft>>>
  /** After anything was answered or skipped: reload, and watch for drafting. */
  onChange: () => void
}) {
  // Questions there when the panel opened; any later one is marked New.
  const [initial] = useState(() => new Set(requests.map((r) => r.id)))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState(false)
  // Questions saved but not yet back in `requests`: they stay animated until
  // the page's reload shows the change, so there is no static gap between the
  // POST returning and the list catching up. `since` is the question's
  // updated_at when the save started; a new one means it landed (status alone
  // can't tell: an edited question was answered before and after).
  const [pending, setPending] = useState<Record<string, { kind: 'answer' | 'skip'; since: string }>>({})
  // Questions that just closed, to fade them into their answered state.
  const [revealed, setRevealed] = useState<Set<string>>(new Set())
  // Answered or skipped questions reopened to change their answer.
  const [editing, setEditing] = useState<Set<string>>(new Set())
  // Answers changed this session: shown "the next draft uses it".
  const [changed, setChanged] = useState<Set<string>>(new Set())

  useEffect(() => {
    const landed = Object.keys(pending).filter((id) => requests.find((r) => r.id === id)?.updated_at !== pending[id].since)
    if (landed.length === 0) return
    const without = <T,>(all: Record<string, T>) => Object.fromEntries(Object.entries(all).filter(([id]) => !landed.includes(id)))
    setPending(without)
    setDrafts(without)
    setRevealed((all) => new Set([...all, ...landed]))
    const edits = landed.filter((id) => editing.has(id))
    if (edits.length) {
      setEditing((all) => new Set([...all].filter((id) => !edits.includes(id))))
      setChanged((all) => new Set([...all, ...edits]))
    }
  }, [requests, pending, editing])
  // If a reload never shows the change, don't leave a question spinning forever.
  useEffect(() => {
    if (Object.keys(pending).length === 0) return
    const id = setTimeout(() => setPending({}), 15000)
    return () => clearTimeout(id)
  }, [pending])
  const unpend = (id: string) => setPending((all) => { const { [id]: _, ...rest } = all; return rest })
  // Shows "Answers submitted" only for a draft these answers set off.
  const [submitted, setSubmitted] = useState(false)

  // The order questions first appeared in, new ones appended: answering never
  // moves anything, even if the API returns them in another order.
  const [order, setOrder] = useState<string[]>(() => requests.map((r) => r.id))
  useEffect(() => {
    const added = requests.filter((r) => !order.includes(r.id)).map((r) => r.id)
    if (added.length) setOrder((o) => [...o, ...added])
  }, [requests, order])
  const rank = (id: string) => { const i = order.indexOf(id); return i < 0 ? Infinity : i }
  const ordered = [...requests].sort((a, b) => rank(a.id) - rank(b.id))
  // Open questions that arrived while the panel was open, until shown.
  const list = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState<Set<string>>(new Set())
  const arrived = ordered.filter((r) => r.status === 'open' && !initial.has(r.id) && !shown.has(r.id))
  const showArrived = () => {
    list.current?.querySelector(`[data-question="${arrived[0].id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setShown((all) => new Set([...all, ...arrived.map((r) => r.id)]))
  }
  const answered = requests.filter((r) => r.status === 'answered').length
  const skipped = requests.filter((r) => r.status === 'dismissed').length
  // A draft's questions hold the section until answered or skipped; setup
  // (intake) ones are optional.
  const requiredLeft = requests.filter((r) => r.status === 'open' && r.origin === 'draft').length
  const allDone = requests.length > 0 && requests.every((r) => r.status !== 'open')
  // The next step's button (draft or redraft): one at a time. Approving is
  // not here: its button is at the end of the draft, so it is read first.
  const [acting, setActing] = useState(false)
  const [actError, setActError] = useState<string | null>(null)
  const act = async (fn: () => Promise<unknown>, failed: string) => {
    setActing(true)
    setActError(null)
    try {
      await fn()
      onChange()
    } catch (e: any) {
      setActError(e.message || failed)
    } finally {
      setActing(false)
    }
  }
  const draft = () => act(() => transitApi.redraftNode(node.transition_id, node.node_key), 'Could not draft this section')
  const answeredAll = 'Every question is answered or skipped.'
  // What to do once every question is answered or skipped, by the section's
  // status (the API approves only from review, and drafts from ready,
  // needs input, failed or review).
  const next: { title: string; body: string; action?: { label: string; run: () => void }; busy?: boolean } | null =
    !allDone ? null
      : node.status === 'ready' ? {
        title: 'Next: draft this section',
        body: `${answeredAll} Draft it from your documents and answers, then review and approve it.`,
        action: { label: 'Let Overmatch draft this section', run: draft },
      }
      : node.status === 'review' ? {
        title: 'Next: approve this section',
        body: `${answeredAll} Read the draft through; Approve section is at the end of it. The sections that wait on this one can then start drafting.`,
      }
      : node.status === 'needs_input' || node.status === 'failed' ? {
        title: 'Next: draft it again',
        body: node.status === 'failed'
          ? `${answeredAll} The last draft failed; draft it again with your answers.`
          : `${answeredAll} Draft the section again so it uses your answers.`,
        action: { label: 'Let Overmatch redraft this section', run: draft },
      }
      : node.status === 'drafting' ? {
        title: 'Drafting from your answers…',
        body: 'When the draft is ready, review it and approve the section.',
        busy: true,
      }
      : node.status === 'blocked' ? {
        title: 'Waiting on earlier sections',
        body: `${answeredAll} This section drafts once ${waitsOn.length ? waitsOn.join(', ') : 'the sections before it'} ${waitsOn.length === 1 ? 'is' : 'are'} approved.`,
      }
      : null

  const update = (id: string, fn: (d: Draft) => Draft) =>
    setDrafts((all) => ({ ...all, [id]: fn(all[id] ?? EMPTY) }))
  // One choice replaces another unless the question allows several; picking
  // the chosen one again clears it either way. Typed text is kept: it adds
  // detail ("Yes" + the names) in both modes.
  const pick = (r: InfoRequest, c: string) =>
    update(r.id, (d) => ({
      ...d,
      picked: d.picked.includes(c) ? d.picked.filter((x) => x !== c)
        : r.allows_multiple_responses ? [...d.picked, c] : [c],
    }))

  const since = (id: string) => requests.find((r) => r.id === id)?.updated_at ?? ''

  const submit = async (id: string) => {
    setBusy(true)
    setPending((all) => ({ ...all, [id]: { kind: 'answer', since: since(id) } }))
    setErrors({})
    try {
      await transitApi.answerInfoRequest(id, answerOf(drafts[id]))
      setSubmitted(true)
      onChange()
    } catch (e: any) {
      unpend(id)
      setErrors({ [id]: e.message || 'Could not save this answer' })
    } finally {
      setBusy(false)
    }
  }

  const skip = async (id: string) => {
    setBusy(true)
    setPending((all) => ({ ...all, [id]: { kind: 'skip', since: since(id) } }))
    setErrors({})
    try {
      await transitApi.dismissInfoRequest(id)
      onChange()
    } catch (e: any) {
      unpend(id)
      setErrors({ [id]: e.message || 'Could not skip this question' })
    } finally {
      setBusy(false)
    }
  }

  // Reopen an answered or skipped question, starting from its answer.
  const edit = (r: InfoRequest) => {
    setEditing((all) => new Set([...all, r.id]))
    setChanged((all) => new Set([...all].filter((id) => id !== r.id)))
    setDrafts((all) => (all[r.id] ? all : { ...all, [r.id]: draftOf(r) }))
  }
  const cancelEdit = (id: string) => {
    setEditing((all) => new Set([...all].filter((x) => x !== id)))
    setDrafts((all) => { const { [id]: _, ...rest } = all; return rest })
    setErrors((all) => { const { [id]: _, ...rest } = all; return rest })
  }
  const [redrafting, setRedrafting] = useState(false)
  const [redraftError, setRedraftError] = useState<string | null>(null)
  const redraft = async () => {
    setRedrafting(true)
    setRedraftError(null)
    try {
      await transitApi.redraftNode(node.transition_id, node.node_key)
      setChanged(new Set())
      setSubmitted(true)
      onChange()
    } catch (e: any) {
      setRedraftError(e.message || 'Could not draft this section again')
    } finally {
      setRedrafting(false)
    }
  }

  return (
    // The Questionnaire tab's content; SidePanel is the frame (tabs, Hide).
    <>
      <div className="flex flex-col gap-2">
        <h2 className="text-lg font-medium leading-tight text-foreground">{node.name} Setup</h2>
        <p className="text-sm leading-none text-tertiary">
          {answered}/{requests.length} answered{skipped > 0 && `, ${skipped} skipped`}
          {requiredLeft > 0 && <span className="font-medium text-status-foreground"> · {requiredLeft} required left</span>}
        </p>
      </div>

      {/* Handholding once every question is answered or skipped: the next
          step for the section's status. */}
      {next && (
        <div key={node.status} className="relative flex animate-fade-in flex-col gap-3 overflow-hidden rounded-lg bg-action-item p-4 text-action-item-foreground">
          {next.busy && <Shimmer />}
          <div className="flex flex-col gap-1 leading-[1.5]">
            <p className="flex items-center gap-2 text-base font-medium">
              {next.busy && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {next.title}
            </p>
            <p className="text-sm opacity-90">{next.body}</p>
          </div>
          {actError && <p className="text-sm">{actError}</p>}
          {next.action && (
            // ghost: the default variant's bg-primary is !important in
            // index.css and would override this fill.
            <Button variant="ghost" onClick={next.action.run} disabled={acting}
              className="h-10 w-fit rounded-[6px] bg-action-item-foreground px-4 text-sm text-action-item shadow-none hover:bg-action-item-foreground/90 hover:text-action-item">
              {acting && <Loader2 className="h-4 w-4 animate-spin" />}
              {next.action.label}
            </Button>
          )}
        </div>
      )}
      {allDone && node.status === 'done' && (
        <p className="flex animate-fade-in items-center gap-2 rounded-lg bg-status-done px-4 py-3 text-sm text-status-foreground">
          <Check className="h-4 w-4" aria-hidden /> Section approved. The sections that wait on it can draft now.
        </p>
      )}
      {/* Floats over the bottom of the panel, never in the flow: the questions
          stay in view and in place while the section re-drafts. */}
      {submitted && node.status === 'drafting' && !allDone && (
        <div role="status" className="absolute inset-x-4 bottom-4 z-10 flex animate-fade-in items-center gap-3 overflow-hidden rounded-lg bg-muted px-4 py-3 text-base leading-6 text-foreground shadow-md">
          <Shimmer />
          <SvgIcon src="/transit/icon-answers-processing.svg" width={20} height={20}
            className="animate-[spin_2s_linear_infinite] motion-reduce:animate-none" />
          <p><span className="font-medium">Answers submitted.</span> Processing answers…</p>
        </div>
      )}
      {arrived.length > 0 && (
        <div role="alert" className="flex shrink-0 animate-fade-in items-center gap-3 rounded-lg bg-status-attention/60 px-4 py-3 text-sm leading-5 text-status-foreground dark:bg-status-attention">
          <MessageCircleQuestion className="h-4 w-4 shrink-0" aria-hidden />
          <p className="flex-1">
            <span className="font-medium">{arrived.length} new question{arrived.length === 1 ? '' : 's'}</span> from the latest draft, at the bottom of the list.
          </p>
          <button type="button" onClick={showArrived}
            className="shrink-0 rounded-[6px] bg-status-foreground px-3 py-1.5 text-xs font-medium text-status-attention hover:opacity-90">
            Show me
          </button>
        </div>
      )}
      {/* Figma's list box, 12px in. It grows with the questions; the whole
          panel scrolls (SidePanel). */}
      <div className="flex shrink-0 flex-col rounded-lg border border-primary p-3">
        <div ref={list} className="flex flex-col gap-4">
          {requests.length === 0 && (
            <p className="text-base text-muted-foreground">No questions for this section.</p>
          )}
          {ordered.map((r, i) => r.status !== 'open' && !editing.has(r.id) ? (
            <ClosedQuestion key={r.id} number={i + 1} request={r} first={i === 0} reveal={revealed.has(r.id)}
              onEdit={!busy ? () => edit(r) : undefined}
              changed={changed.has(r.id)}
              onRedraft={changed.has(r.id) && REDRAFTABLE.includes(node.status) ? redraft : undefined}
              redrafting={redrafting} redraftError={redraftError} />
          ) : (
            <Question
              key={r.id}
              number={i + 1}
              request={r}
              draft={drafts[r.id] ?? EMPTY}
              error={errors[r.id]}
              busy={busy}
              onPick={(c) => pick(r, c)}
              onType={(typed) => update(r.id, (d) => ({ ...d, typed }))}
              onStartTyping={() => update(r.id, (d) => ({ ...d, typing: true }))}
              onSkip={() => skip(r.id)}
              onSubmit={() => submit(r.id)}
              onCancel={editing.has(r.id) ? () => cancelEdit(r.id) : undefined}
              pending={pending[r.id]?.kind}
              isNew={!initial.has(r.id)}
              first={i === 0}
            />
          ))}
        </div>
      </div>
    </>
  )
}

// A draft's follow-up questions sit on a tinted card; setup questions are rows
// split by a rule.
const itemClass = (r: InfoRequest, first: boolean) =>
  r.origin === 'draft' ? 'rounded-md bg-state-warning-subtle p-3' : !first && 'border-t border-border-subtle pt-4'

function Question({ number, request, draft, error, busy, onPick, onType, onStartTyping, onSkip, onSubmit, onCancel, pending, isNew, first }: {
  number: number
  request: InfoRequest
  draft: Draft
  error?: string
  busy: boolean
  onPick: (choice: string) => void
  onType: (text: string) => void
  onStartTyping: () => void
  onSkip: () => void
  onSubmit: () => void
  /** Set when changing an earlier answer: no Skip (the API skips only open
   * questions), and Cancel puts the answer back as it was. */
  onCancel?: () => void
  /** Saving: answered or skipped, waiting for the list to show it. */
  pending?: 'answer' | 'skip'
  /** Raised since the panel opened (a re-draft asked it). */
  isNew: boolean
  first: boolean
}) {
  const hasChoices = request.choices.length > 0
  const locked = busy || !!pending
  // Required: a draft raised it and it holds the section until answered (or
  // skipped). Setup questions are optional.
  const required = request.origin === 'draft' && request.status === 'open'
  return (
    // shrink-0: overflow-hidden (for the shimmer) would otherwise let the
    // scrolling list squash a question to its title instead of scrolling.
    <div data-question={request.id} aria-busy={!!pending} className={cn('relative flex shrink-0 flex-col gap-4 overflow-hidden', itemClass(request, first), required && 'pl-4', isNew && 'animate-fade-in')}>
      {pending && <Shimmer />}
      {/* The required ones' bar, down the card's left edge: the Needs input dot's amber. */}
      {required && <span aria-hidden className="absolute inset-y-0 left-0 w-1 bg-warning" />}
      <div className="flex items-start gap-3 text-base font-medium leading-[1.5]">
        <span className="text-tertiary">{number}</span>
        <span className="flex-1 text-foreground">{request.question}</span>
        {required ? (
          <span className="shrink-0 rounded-[4px] bg-status-attention px-1.5 py-0.5 text-xs font-medium leading-4 text-status-foreground dark:bg-warning dark:text-neutral-950">Required</span>
        ) : request.origin === 'intake' && (
          <span className="shrink-0 rounded-[4px] bg-secondary px-1.5 py-0.5 text-xs font-medium leading-4 text-tertiary">Optional</span>
        )}
      </div>

      <div className={cn('flex flex-col gap-1 transition-opacity duration-300', pending && 'pointer-events-none opacity-60')}>
        {request.choices.map((c) => {
          const on = draft.picked.includes(c)
          return (
            <button key={c} type="button" aria-pressed={on} onClick={() => onPick(c)} disabled={locked}
              className={cn(
                'flex w-full items-center justify-between gap-3 rounded-md px-4 py-2 text-left text-base leading-[1.5] transition-colors',
                on ? 'bg-primary text-primary-foreground' : 'bg-foreground/[0.04] dark:bg-foreground/[0.08] text-muted-foreground hover:bg-secondary-hover hover:text-foreground'
              )}>
              {c}
              {on && <Check className="h-4 w-4 shrink-0" aria-hidden />}
            </button>
          )
        })}
        {hasChoices && !draft.typing ? (
          <button type="button" onClick={onStartTyping} disabled={locked}
            className="w-full rounded-md bg-foreground/[0.04] dark:bg-foreground/[0.08] px-4 py-2 text-left text-base leading-[1.5] text-muted-foreground transition-colors hover:bg-secondary-hover hover:text-foreground">
            Type another response
          </button>
        ) : (
          <Textarea
            rows={hasChoices ? 2 : 3}
            autoFocus={hasChoices}
            value={draft.typed}
            onChange={(e) => onType(e.target.value)}
            disabled={locked}
            placeholder={hasChoices ? 'Type another response' : 'Your answer'}
            // Flat like the choice rows; focus is a thin inset primary line
            // (inset, so it stays inside the box in the scrolling list).
            className="resize-none rounded-[12px] border-0 bg-foreground/[0.04] dark:bg-foreground/[0.08] px-5 py-4 text-base leading-[1.5] shadow-none focus:bg-card focus:ring-1 focus:ring-inset focus:ring-primary"
          />
        )}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      {/* Header is the question and whether it's required; what's happening
          to it sits here, beside its buttons. */}
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 flex-wrap items-center gap-2 text-sm text-tertiary">
          {pending ? (
            <span className="flex items-center gap-1.5">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {pending === 'skip' ? 'Skipping…' : 'Saving answer…'}
            </span>
          ) : onCancel ? (
            'Changing answer'
          ) : (
            <>
              {isNew && <span className="rounded-[4px] bg-status-attention px-1.5 py-0.5 text-xs font-medium text-status-foreground">New</span>}
              {request.allows_multiple_responses && hasChoices && <span>Pick any</span>}
            </>
          )}
        </span>
        <div className="flex shrink-0 gap-1">
          {/* Skip sits with Submit; there's none when changing an answer (the
              API skips only open questions), where Cancel takes its place. */}
          {!onCancel && (
            <Button size="sm" variant="ghost" onClick={onSkip} disabled={locked}
              className="h-8 rounded-[6px] px-4 text-sm text-tertiary shadow-none hover:bg-secondary-hover hover:text-foreground">
              Skip
            </Button>
          )}
          {onCancel && (
            <Button size="sm" variant="ghost" onClick={onCancel} disabled={locked}
              className="h-8 rounded-[6px] px-4 text-sm shadow-none hover:bg-secondary-hover">
              Cancel
            </Button>
          )}
          <Button size="sm" onClick={onSubmit} disabled={locked || !answerOf(draft)}
            className="h-8 rounded-[6px] px-4 text-sm shadow-none">
            {pending === 'answer' && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            {pending === 'answer' ? 'Saving…' : 'Submit'}
          </Button>
        </div>
      </div>
    </div>
  )
}

/**
 * An answered or skipped question, left where it was, with Edit (or Answer,
 * after a skip). The answer is split back into the choices it picked and
 * anything typed.
 */
function ClosedQuestion({ number, request, first, reveal, onEdit, changed, onRedraft, redrafting, redraftError }: {
  number: number
  request: InfoRequest
  first: boolean
  reveal: boolean
  /** Reopen it to change the answer. */
  onEdit?: () => void
  /** Its answer was changed: the next draft reads it, nothing re-drafts now. */
  changed: boolean
  /** Draft the section again now, when its status allows. */
  onRedraft?: () => void
  redrafting: boolean
  redraftError: string | null
}) {
  const answered = request.status === 'answered'
  const { picked, typed } = draftOf(request)
  return (
    <div className={cn('flex shrink-0 flex-col gap-4', itemClass(request, first), reveal && 'animate-fade-in')}>
      <div className="flex items-start gap-3 text-base font-medium leading-[1.5]">
        <span className="text-tertiary">{number}</span>
        <span className={cn('flex-1', answered ? 'text-foreground' : 'text-tertiary')}>{request.question}</span>
      </div>
      {answered && (
        <div className="flex flex-col gap-1">
          {request.choices.map((c) => (
            <div key={c} className={cn(
              'flex w-full items-center justify-between gap-3 rounded-md px-4 py-2 text-base leading-[1.5]',
              picked.includes(c) ? 'bg-primary text-primary-foreground' : 'bg-foreground/[0.04] dark:bg-foreground/[0.08] text-muted-foreground opacity-50'
            )}>
              {c}
              {picked.includes(c) && <Check className="h-4 w-4 shrink-0" aria-hidden />}
            </div>
          ))}
          {typed && (
            <div className="whitespace-pre-wrap rounded-[12px] bg-foreground/[0.04] px-5 py-4 text-base leading-[1.5] text-foreground dark:bg-foreground/[0.08]">
              {typed}
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <span className="flex items-center gap-1 text-sm text-tertiary">
          {answered ? <><Check className="h-4 w-4" aria-hidden /> Answered</> : 'Skipped'}
        </span>
        {onEdit && (
          <Button size="sm" variant="ghost" onClick={onEdit}
            className="h-8 rounded-[6px] px-4 text-sm text-tertiary shadow-none hover:bg-secondary-hover hover:text-foreground">
            {answered ? 'Edit' : 'Answer'}
          </Button>
        )}
      </div>
      {changed && (
        <div className="flex animate-fade-in items-center justify-between gap-3 rounded-md bg-status-attention/40 px-4 py-2 text-sm text-foreground">
          <span>
            Answer saved. The next draft of this section will use it.
            {redraftError && <span className="block text-destructive">{redraftError}</span>}
          </span>
          {onRedraft && (
            <Button size="sm" variant="ghost" onClick={onRedraft} disabled={redrafting}
              className="h-8 shrink-0 rounded-[6px] bg-card px-3 text-sm shadow-none hover:bg-secondary-hover">
              {redrafting && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Let Overmatch redraft this section
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
